import { Component, ElementRef, OnInit, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { DtInstallationDetails } from 'src/app/models/installation.model';
import { InstallationService } from 'src/app/services/installation.service';
import { AuthService, PermissionsService } from 'shared-lib';
import { User } from 'src/app/models/user.model';
import { UserService } from 'src/app/services/user.service';
import { DeferredToastService } from 'src/app/services/deferred-toast.service';
import { ImageService } from 'src/app/services/image.service';

@Component({
  selector: 'app-dt-meter-installation-details',
  templateUrl: './dt-meter-installation-details.component.html',
  styleUrls: ['./dt-meter-installation-details.component.scss'],
})
export class DtMeterInstallationDetailsComponent implements OnInit {
  id: string = '';
  selectedInstallation: DtInstallationDetails = new DtInstallationDetails();
  selectedImage: any;
  currentRotation: number = 0;
  isImageMapVisible: boolean = false;
  imgElement: any;
  imgZoomFullContainer: any;
  hasCimDataIntegration: boolean = false;
  declineNote: string = '';
  rejectionNote: string = '';
  filterText: string = '';
  tempAssignedUserId: number = 0;
  mobileUsers: User[] = [];
  isDropdownOpened: boolean = false;
  rejectToOption: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    private installationService: InstallationService,
    private el: ElementRef,
    private renderer: Renderer2,
    private authService: AuthService,
    public permissionsService: PermissionsService,
    private userService: UserService,
    private deferredToastService: DeferredToastService,
    private imageService: ImageService
  ) {
    this.hasCimDataIntegration = this.authService.hasCimDataIntegration;
  }

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id') || '';
    this.imgElement = this.el?.nativeElement?.querySelector('.ngxImageZoomThumbnail');
    this.imgZoomFullContainer = this.el?.nativeElement?.querySelector('.ngxImageZoomFull');

    this.getSelectedInstallation(parseInt(this.id));
    this.deferredToastService.showQueuedMessage();
  }

  getSelectedInstallation(id: number) {
    this.installationService.getDtDetailsById(id).subscribe(
      (data) => {
        var response = data.body;
        if (response?.status === 'OK') {
          this.selectedInstallation = new DtInstallationDetails(response.data);
          const onAwaitingRoute = this.router.url.includes('/dt/awaiting-approval/');
          const isAwaiting = this.selectedInstallation.installationStatus == 'AwaitingApproval';
          if (isAwaiting && !onAwaitingRoute) {
            this.router.navigate(['/installations/dt/awaiting-approval', id]);
          } else if (!isAwaiting && onAwaitingRoute) {
            this.router.navigate(['/installations/dt/details', id]);
          }
        }

        if (response?.status === 'BadRequest') {
          this.toastr.error(response.message);
        }
      },
      (error) => {
        this.toastr.error('An error ocurred.');
      }
    );
  }

  get hasOldMeter(): boolean {
    return !!this.selectedInstallation.oldMeterNumber;
  }

  getImageByType(imageType: string) {
    return this.selectedInstallation.images?.find(x => x.imageType === imageType);
  }

  getImageUrl(imageType: string): string | undefined {
    return this.getImageByType(imageType)?.url;
  }

  showImage(imageType: string) {
    this.selectedImage = this.getImageByType(imageType);
    this.getSelectedImageAddress();
  }

  getSelectedImageAddress() {
    const image = this.selectedImage;
    if (!image?.latitude || !image?.longitude || image.physicalAddress) return;

    this.imageService.getAddress({ latitude: image.latitude, longitude: image.longitude }).subscribe((data) => {
      if (data.body?.status === 'OK') {
        image.physicalAddress = data.body.data;
      }
    });
  }

  onModalFade() {
    this.currentRotation = 0;

    if (this.imgZoomFullContainer) {
      var displayValue = getComputedStyle(this.imgZoomFullContainer).display;

      if (displayValue == 'block') {
        this.imgElement.click();
      }
    }
  }

  rotateLeft() {
    this.currentRotation -= 90;
  }

  rotateRight() {
    this.currentRotation += 90;
  }

  onModalOpen() {
    this.renderer.setStyle(document.body, 'overflow', 'auto');
    this.renderer.setStyle(document.body, 'overflow-x', 'hidden');
    this.renderer.setStyle(document.body, 'padding-right', '0');
  }

  accept() {
    const id = parseInt(this.id);
    this.installationService.acceptInstallation(id).subscribe({
      next: (response) => {
        if (response?.status === 200 || response?.status === 'OK') {
          this.deferredToastService.queueSuccess('Installation Accepted');
          window.location.href = `/installations/dt/awaiting-approval/${id}`;
        } else {
          this.toastr.error(response?.message);
        }
      },
      error: () => this.toastr.error('An error occurred.'),
    });
  }

  decline() {
    if (!this.declineNote || !this.declineNote.trim()) {
      this.toastr.error('Please enter a reason.');
      return;
    }
    let obj = { id: parseInt(this.id), note: this.declineNote.trim() };
    this.installationService.declineInstallation(obj).subscribe({
      next: (response) => {
        if (response?.status === 200 || response?.status === 'OK') {
          this.deferredToastService.queueSuccess('Installation Declined');
          window.location.reload();
        } else {
          this.toastr.error(response?.message);
        }
      },
      error: () => this.toastr.error('An error occurred.'),
    });
  }

  recall() {
    const id = parseInt(this.id);
    const wasAwaitingApproval = this.selectedInstallation.installationStatus == 'AwaitingApproval';
    this.installationService.recallInstallation(id).subscribe({
      next: (data) => {
        const response = data.body;
        if (response?.status === 200 || response?.status === 'OK') {
          this.deferredToastService.queueSuccess('Installation Recalled');
          if (wasAwaitingApproval) {
            window.location.href = `/installations/dt/details/${id}`;
          } else {
            window.location.reload();
          }
        } else {
          this.toastr.error(response?.message);
        }
      },
      error: () => this.toastr.error('An error occurred.'),
    });
  }

  approve() {
    this.installationService.approveInstallation(parseInt(this.id)).subscribe({
      next: (response) => {
        if (response?.status === 200 || response?.status === 'OK') {
          this.deferredToastService.queueSuccess('Installation Approved');
          window.location.reload();
        } else {
          this.toastr.error(response?.message);
        }
      },
      error: () => this.toastr.error('An error occurred.'),
    });
  }

  reject() {
    let obj = {
      id: parseInt(this.id),
      note: this.rejectionNote,
      assignToId: this.tempAssignedUserId,
    };
    if (this.rejectToOption === 'fieldAgent' && this.tempAssignedUserId === 0) {
      this.toastr.error('Select field Agent');
      return;
    }
    this.installationService.rejectInstallation(obj).subscribe({
      next: (response) => {
        if (response?.status === 200 || response?.status === 'OK') {
          this.deferredToastService.queueSuccess('Installation Rejected');
          window.location.reload();
        } else {
          this.toastr.error(response?.message);
        }
      },
      error: () => this.toastr.error('An error occurred.'),
    });
  }

  onOptionChange(event: Event) {
    const target = event.target as HTMLSelectElement;
    this.rejectToOption = target.value;
    this.tempAssignedUserId = 0;
    this.filterText = '';
  }

  filterMobileUsers() {
    if (this.filterText.length < 3) return;
    this.isDropdownOpened = true;
    this.tempAssignedUserId = 0;
    this.getMobileUsers();
  }

  getMobileUsers() {
    this.userService.getMobileUsers(0, this.filterText).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.mobileUsers = responseData.data;
          if (this.mobileUsers.length == 0) this.isDropdownOpened = false;
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: () => this.toastr.error('Error occured'),
    });
  }

  selectFieldAgent(id: number) {
    this.isDropdownOpened = false;
    this.tempAssignedUserId = id;
    var mobUser = this.mobileUsers.find((x) => x.id == id);
    if (mobUser?.firstName && mobUser?.lastName) {
      this.filterText = mobUser!.firstName + ' ' + mobUser?.lastName;
    } else {
      this.filterText = mobUser!.firstName;
    }
  }
}
